import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Shimmer } from "@/components/Shimmer";
import {
  ArrowLeft, FileText, Image as ImageIcon, File, ExternalLink, Clock, Download, Copy,
  ChevronLeft, ChevronRight, Eye, EyeOff,
} from "lucide-react";
import { fmtDateTime, fmtSeconds, fmtBytes, fmtCost, fmtNum, extractName } from "@/lib/format";
import { toast } from "sonner";
import { getNeighbors } from "@/lib/emailNav";

export default function EmailDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [showMapping, setShowMapping] = useState(true);
  const neighbors = getNeighbors(id);

  useEffect(() => {
    setData(null);
    api.get(`/emails/${id}`).then((r) => setData(r.data)).catch((e) => setErr(e.message));
  }, [id]);

  // Keyboard shortcuts (only when not typing in an input)
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      const isTyping = t?.tagName === "INPUT" || t?.tagName === "TEXTAREA" || t?.isContentEditable;
      if (isTyping) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === "j" && neighbors.next) { e.preventDefault(); nav(`/emails/${neighbors.next}`); }
      else if (k === "k" && neighbors.prev) { e.preventDefault(); nav(`/emails/${neighbors.prev}`); }
      else if (k === "e") { e.preventDefault(); setShowMapping((v) => !v); }
      else if (k === "o") {
        const oppId = data?.salesforce?.opportunity_id;
        if (oppId) { e.preventDefault(); nav(`/opportunities/${oppId}`); }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [nav, neighbors.next, neighbors.prev, data?.salesforce?.opportunity_id]);

  if (err) return <div className="p-8 text-rose-700">Error: {err}</div>;
  if (!data) return <div className="p-8 space-y-3"><Shimmer className="h-10 w-1/3" /><Shimmer className="h-64" /></div>;

  const sf = data.salesforce || {};

  return (
    <div className="p-8 max-w-[1700px]" data-testid="email-detail-page">
      <div className="flex items-center justify-between mb-4">
        <Link to="/emails" className="inline-flex items-center gap-1.5 text-xs font-mono text-muted-foreground hover:text-zinc-900">
          <ArrowLeft size={14} /> Back to emails
        </Link>
        <div className="flex items-center gap-2" data-testid="email-nav-bar">
          {neighbors.total > 0 && neighbors.index >= 0 && (
            <span className="font-mono text-[11px] text-muted-foreground">
              {neighbors.index + 1} of {neighbors.total}
            </span>
          )}
          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-1.5"
            onClick={() => neighbors.prev && nav(`/emails/${neighbors.prev}`)}
            disabled={!neighbors.prev}
            data-testid="email-prev-btn"
            title="Previous email (K)"
          >
            <ChevronLeft size={14} /> <span className="kbd">K</span>
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-1.5"
            onClick={() => neighbors.next && nav(`/emails/${neighbors.next}`)}
            disabled={!neighbors.next}
            data-testid="email-next-btn"
            title="Next email (J)"
          >
            <span className="kbd">J</span> <ChevronRight size={14} />
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-8 gap-1.5"
            onClick={() => setShowMapping((v) => !v)}
            data-testid="email-toggle-mapping-btn"
            title="Toggle extraction panel (E)"
          >
            {showMapping ? <EyeOff size={13} /> : <Eye size={13} />}
            <span className="kbd">E</span>
          </Button>
          {sf.opportunity_id && (
            <Button
              size="sm"
              variant="outline"
              className="h-8 gap-1.5"
              onClick={() => nav(`/opportunities/${sf.opportunity_id}`)}
              data-testid="email-open-opp-btn"
              title="Open opportunity (O)"
            >
              <ExternalLink size={13} /> Opp <span className="kbd">O</span>
            </Button>
          )}
        </div>
      </div>

      {/* Header */}
      <header className="pb-6 mb-6 border-b border-border">
        <div className="overline mb-2">Email · {data.id.slice(0, 8)}</div>
        <h1 className="font-serif text-3xl sm:text-4xl tracking-tight leading-[1.1] mb-3" data-testid="email-subject">
          {data.subject || "(no subject)"}
        </h1>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-mono text-muted-foreground">
          <span>from <b className="text-zinc-900">{extractName(data.from_addr)}</b></span>
          <span>{fmtDateTime(data.received_at || data.ingested_at)}</span>
          <span className="flex items-center gap-1.5"><StatusBadge status={data.processing_status} /></span>
          {data.classification?.category && (
            <span>category <b className="text-zinc-900">{data.classification.category}</b>
              {data.classification.confidence && <span className="text-muted-foreground"> ({data.classification.confidence.toFixed(2)})</span>}
            </span>
          )}
          {sf.opportunity_id && (
            <span className="flex items-center gap-1.5">
              SF Opp <Link to={`/opportunities/${sf.opportunity_id}`} className="text-blue-700 hover:underline">{sf.opportunity_id}</Link>
              <button onClick={() => { navigator.clipboard.writeText(sf.opportunity_id); toast("Copied"); }} className="p-1 hover:bg-zinc-100 rounded"><Copy size={11}/></button>
            </span>
          )}
          {data.llm_cost_usd !== null && data.llm_cost_usd !== undefined && (
            <span>LLM cost <b className="text-zinc-900">{fmtCost(data.llm_cost_usd)}</b>
              <span className="text-muted-foreground"> ({fmtNum(data.llm_input_tokens)} in / {fmtNum(data.llm_output_tokens)} out)</span>
            </span>
          )}
        </div>
      </header>

      <div className="grid grid-cols-12 gap-4">
        {/* Body panel */}
        <section
          className={`col-span-12 ${showMapping ? "lg:col-span-7" : "lg:col-span-12"} card-surface min-h-[400px]`}
          data-testid="body-panel"
        >
          <div className="px-4 py-2 border-b border-border flex items-center justify-between">
            <div className="overline">Original email</div>
            <Tabs defaultValue="text" className="ml-auto">
              <TabsList className="h-7">
                <TabsTrigger value="text" className="text-xs" data-testid="tab-body-text">Text</TabsTrigger>
                {data.body_html && <TabsTrigger value="html" className="text-xs" data-testid="tab-body-html">HTML</TabsTrigger>}
              </TabsList>
              <TabsContent value="text" className="mt-0">
                <div className="p-5 whitespace-pre-wrap text-sm leading-relaxed font-sans max-h-[640px] overflow-auto">
                  {data.body_text || data.snippet || <span className="text-muted-foreground">No text body.</span>}
                </div>
              </TabsContent>
              <TabsContent value="html" className="mt-0">
                <div className="p-5 text-sm leading-relaxed prose prose-sm max-w-none max-h-[640px] overflow-auto"
                     dangerouslySetInnerHTML={{ __html: data.body_html || "" }} />
              </TabsContent>
            </Tabs>
          </div>
        </section>

        {/* Field mapping */}
        {showMapping && (
        <section className="col-span-12 lg:col-span-5 card-surface min-h-[400px]" data-testid="mapping-panel">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <div className="overline">Field mapping · email → Salesforce</div>
            <span className="font-mono text-[10px] text-muted-foreground">{data.field_mapping.length} fields</span>
          </div>
          <div className="overflow-auto max-h-[600px]">
            <table className="w-full text-xs">
              <thead className="bg-zinc-50">
                <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground">
                  <th className="px-3 py-2 font-medium">Source</th>
                  <th className="px-3 py-2 font-medium">SF field</th>
                  <th className="px-3 py-2 font-medium">Value</th>
                  <th className="px-3 py-2 font-medium">Conf</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border font-mono">
                {data.field_mapping.map((f, i) => (
                  <tr key={i} className="hover:bg-zinc-50 align-top">
                    <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{f.source}</td>
                    <td className="px-3 py-2">
                      <span className="text-zinc-500">{f.salesforce_object}.</span>
                      <span className="text-zinc-900 font-medium">{f.salesforce_field}</span>
                    </td>
                    <td className="px-3 py-2 max-w-[220px] truncate" title={String(f.value)}>{String(f.value)}</td>
                    <td className="px-3 py-2 text-[10px] text-muted-foreground">{String(f.confidence)}</td>
                  </tr>
                ))}
                {data.field_mapping.length === 0 && (
                  <tr><td colSpan={4} className="text-center py-6 text-muted-foreground">No extracted fields.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
        )}

        {/* Attachments */}
        <section className="col-span-12 lg:col-span-7 card-surface" data-testid="attachments-panel">
          <div className="px-4 py-3 border-b border-border">
            <div className="overline">Attachments · {data.attachments.length}</div>
          </div>
          <div className="p-4 space-y-3">
            {data.attachments.length === 0 && (
              <div className="text-sm text-muted-foreground">No attachments</div>
            )}
            {data.attachments.map((a) => <AttachmentCard key={a.id} emailId={data.id} att={a} />)}
          </div>
        </section>

        {/* Timeline */}
        <section className="col-span-12 lg:col-span-5 card-surface" data-testid="timeline-panel">
          <div className="px-4 py-3 border-b border-border flex items-center gap-2">
            <Clock size={12} /><span className="overline">Processing timeline</span>
          </div>
          <ol className="p-4 space-y-3">
            {data.timeline.length === 0 && <div className="text-sm text-muted-foreground">No events yet.</div>}
            {data.timeline.map((ev) => (
              <li key={ev.id} className="flex items-start gap-3">
                <div className={`mt-1.5 h-2 w-2 rounded-full ${ev.status === "completed" ? "bg-emerald-500" : ev.status === "error" ? "bg-rose-500" : "bg-zinc-300"}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <div className="text-sm font-medium capitalize">{ev.node}</div>
                    <div className="font-mono text-xs text-muted-foreground">
                      {ev.duration_seconds !== null ? fmtSeconds(ev.duration_seconds) : "—"}
                    </div>
                  </div>
                  <div className="font-mono text-[11px] text-muted-foreground">{fmtDateTime(ev.ts)}</div>
                  {ev.payload && Object.keys(ev.payload).length > 0 && (
                    <details className="mt-1">
                      <summary className="text-[11px] text-zinc-500 cursor-pointer hover:text-zinc-900 font-mono">payload</summary>
                      <pre className="mt-1 text-[10px] bg-zinc-50 p-2 rounded overflow-auto max-h-40">
                        {JSON.stringify(ev.payload, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* LLM invocations */}
        {data.llm_invocations && data.llm_invocations.length > 0 && (
          <section className="col-span-12 card-surface" data-testid="llm-invocations-panel">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <div className="overline">LLM invocations · {data.llm_invocations.length}</div>
              <div className="font-mono text-xs text-muted-foreground">
                total <b className="text-zinc-900">{fmtCost(data.llm_cost_usd)}</b> ·{" "}
                {fmtNum(data.llm_input_tokens)} in / {fmtNum(data.llm_output_tokens)} out
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="px-4 py-2 font-medium">Node</th>
                    <th className="px-4 py-2 font-medium">Model</th>
                    <th className="px-4 py-2 font-medium text-right">Input</th>
                    <th className="px-4 py-2 font-medium text-right">Output</th>
                    <th className="px-4 py-2 font-medium text-right">Cost</th>
                    <th className="px-4 py-2 font-medium text-right">Duration</th>
                    <th className="px-4 py-2 font-medium">Timestamp</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border font-mono">
                  {data.llm_invocations.map((inv) => (
                    <tr key={inv.id} className="hover:bg-zinc-50" data-testid={`llm-inv-${inv.id}`}>
                      <td className="px-4 py-2 text-sm font-sans capitalize">{inv.node}</td>
                      <td className="px-4 py-2 text-[11px]">{inv.model}</td>
                      <td className="px-4 py-2 text-xs text-right">{fmtNum(inv.input_tokens)}</td>
                      <td className="px-4 py-2 text-xs text-right">{fmtNum(inv.output_tokens)}</td>
                      <td className="px-4 py-2 text-xs text-right text-zinc-900 font-medium">{fmtCost(inv.cost_usd)}</td>
                      <td className="px-4 py-2 text-xs text-right text-muted-foreground">
                        {inv.duration_ms ? `${(inv.duration_ms / 1000).toFixed(1)}s` : "—"}
                      </td>
                      <td className="px-4 py-2 text-[11px] text-muted-foreground">{fmtDateTime(inv.ts)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function AttachmentCard({ emailId, att }) {
  const [open, setOpen] = useState(false);
  const [urlData, setUrlData] = useState(null); // { url, expires_at, ... }
  const [loading, setLoading] = useState(false);

  const icon = att.mime_type?.startsWith("image/")
    ? <ImageIcon size={20} />
    : att.mime_type === "application/pdf"
    ? <FileText size={20} />
    : <File size={20} />;

  const isFresh = () => {
    if (!urlData?.expires_at) return false;
    return (new Date(urlData.expires_at).getTime() - Date.now()) > 30000; // 30s buffer
  };

  const fetchUrl = async () => {
    if (isFresh()) return urlData;
    setLoading(true);
    try {
      const { data } = await api.get(`/emails/${emailId}/attachments/${att.id}/url`);
      setUrlData(data);
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not fetch attachment URL");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const togglePreview = async () => {
    if (!open) {
      const d = await fetchUrl();
      if (d) setOpen(true);
    } else {
      setOpen(false);
    }
  };

  const openInNewTab = async () => {
    // Must be called from user gesture, so open a blank tab synchronously and redirect after fetch.
    const tab = window.open("", "_blank", "noopener");
    const d = await fetchUrl();
    if (d?.url && tab) tab.location.href = d.url;
    else if (d?.url) window.location.assign(d.url);
    else if (tab) tab.close();
  };

  return (
    <div className="border border-border rounded-md overflow-hidden" data-testid={`attachment-${att.id}`}>
      <div className="flex items-center gap-3 p-3 bg-zinc-50">
        <div className="h-10 w-10 rounded bg-white border border-border grid place-items-center text-zinc-700">
          {icon}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">{att.filename}</div>
          <div className="font-mono text-[11px] text-muted-foreground">
            {att.mime_type} · {fmtBytes(att.size_bytes)} · {att.sha256?.slice(0, 10)}…
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={togglePreview} disabled={loading} data-testid={`att-preview-${att.id}`}>
          {loading ? "…" : open ? "Hide" : "Preview"}
        </Button>
        <Button size="sm" variant="outline" onClick={openInNewTab} disabled={loading} data-testid={`att-download-${att.id}`} title="Open in new tab">
          <Download size={14} />
        </Button>
      </div>
      {open && urlData?.url && (
        <div className="p-3 bg-white">
          {att.mime_type?.startsWith("image/") && (
            <img src={urlData.url} alt={att.filename} className="max-h-96 rounded border border-border" />
          )}
          {att.mime_type === "application/pdf" && (
            <iframe
              title={att.filename}
              src={urlData.url}
              className="w-full h-[500px] border border-border rounded"
            />
          )}
          {att.extracted_preview && (
            <details className="mt-3">
              <summary className="overline cursor-pointer">Extracted text preview</summary>
              <pre className="mt-2 text-[11px] font-mono bg-zinc-50 border border-border rounded p-3 whitespace-pre-wrap max-h-80 overflow-auto">
                {att.extracted_preview}
              </pre>
            </details>
          )}
          {urlData.expires_at && (
            <div className="mt-2 text-[10px] mono text-muted-foreground">
              presigned link expires {new Date(urlData.expires_at).toLocaleTimeString()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
